import { useEffect, useMemo, type FunctionComponent } from "react";
import {
  ArrowLeftOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  IdcardOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import { Elements } from "@stripe/react-stripe-js";
import { useQuery } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { Avatar, Button, Card, Descriptions, Dropdown, List, Menu, Space, Spin } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { productActions, productClientActions, RequestStatus } from "@fitness-app/app-store";
import {
  PaymentTypeOption,
  ProductDurationType,
  type OrderPaymentType,
} from "@fitness-app/data-models/entities/Product";
import { TraineeStatus } from "@fitness-app/data-models/entities/Trainee";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { getUserInitials } from "@fitness-app/utils";

import ModalForm from "~/components/ModalForm/ModalForm";
import ClientAutomationLogs from "~/modules/Products/ProductClientDetails/ClientAutomationLogs/ClientAutomationLogs";
import ProductClientActionsHistory from "~/modules/Products/ProductClientDetails/ProductClientActionsHistory/ProductClientActionsHistory";
import {
  ProductClientArchive,
  useProductClientArchive,
} from "~/modules/Products/ProductClientDetails/ProductClientArchive/ProductClientArchive";
import {
  ProductClientEmailChange,
  useProductClientEmailChange,
} from "~/modules/Products/ProductClientDetails/ProductClientEmailChange/ProductClientEmailChange";
import ProductClientInvoiceData from "~/modules/Products/ProductClientDetails/ProductClientInvoiceData/ProductClientInvoiceData";
import { ProductClientScheduledAccess } from "~/modules/Products/ProductClientDetails/ProductClientScheduledAccess/ProductClientScheduledAccess";
import ProductClientNotes from "~/modules/Products/ProductClientDetails/ProductClientNotes/ProductClientNotes";
import ProductClientSettings from "~/modules/Products/ProductClientDetails/ProductClientSettings/ProductClientSettings";
import ClientStatusBadge from "~/modules/Trainee/components/TraineeStatusBadge/TraineeStatusBadge";
import { usePermission, usePermissionRequirement } from "~/hooks/usePermission";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { supabase, useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  productId: string;
  ownerId?: string;
  readonly: boolean;
  /** Podany wprost, gdy ekran jest osadzony (drawer) zamiast stać na trasie. */
  clientEmail?: string;
  /** Tryb osadzony: bez powrotu i bez ramki karty. */
  embedded?: boolean;
}

type Props = OwnProps;

const allowedPaymentTypes: OrderPaymentType[] = [PaymentTypeOption.OneTime, PaymentTypeOption.Recurring];

const ProductClientDetails: FunctionComponent<Props> = ({
  productId,
  ownerId,
  readonly,
  clientEmail: clientEmailFromProps,
  embedded = false,
}) => {
  const { t } = useTranslation(["client", "products", "common"]);
  const { clientEmail: clientEmailFromRoute } = useParams<{ clientEmail: string }>();
  const clientEmail = clientEmailFromProps ?? clientEmailFromRoute;
  // Sekcje sprzedażowe — historia zamówień z kwotami, dane do faktury, zwrot —
  // zostają przy koncie zarządzającym produktami. Trener widzi dostęp i notatki.
  const canManageProducts = usePermission(PermissionKey.PRODUCTS_MANAGE, PermissionScope.ALL);
  const { showArchiveModal, selectedClient, setClient, handleSubmit, processing } = useProductClientArchive();
  const {
    handleEmailChangeSubmit,
    showChangeEmailModal,
    setClientToEmailChange,
    selectedClientToChangeEmail,
    processingChangingEmail,
  } = useProductClientEmailChange();
  const { data, status } = useAppSelector((store) => store.productClient);
  // The timeline builds the current plan's instalments from `accessPeriod` alone, so it is
  // worth showing for every instalment client — not only for the ones with something queued.
  // Gating it on the queue hid it for every BLIK plan, which never fills `scheduledAccessPeriod`
  // (TRE-2031), and for every plain card instalment plan too.
  const scheduledAccess = data && "scheduledAccessPeriod" in data ? (data.scheduledAccessPeriod ?? []) : [];
  const hasInstallmentPlan = Boolean(
    data?.accessPeriod && "installments" in data.accessPeriod && data.accessPeriod.installments,
  );
  const { data: productAutomation } = useAppSelector((store) => store.productAutomation);
  const productDetails = useAppSelector((store) => store.product.details);
  const navigate = useNavigate();

  // The archive/refund backend refunds the order pointed at by product_client.lastOrder,
  // not simply the newest one — mirror that here so the modal shows the right options.
  // No fallback to orders[0]: showing a different order's amount next to a refund
  // the backend applies to lastOrder would be misleading.
  // NOTE: fetchProductClientOrders fills productClient.clientOrders; productOrders.orders
  // belongs to the product-level transactions view and is empty on this page.
  const orders = useAppSelector((store) => store.productClient.clientOrders);
  const lastOrder = orders.find((order) => order.id === data?.lastOrder) ?? null;

  const dispatch = useAppDispatch();

  const stripePromise = useMemo(() => {
    return loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY, {
      locale: "pl",
    });
  }, []);

  useEffect(() => {
    if (productId && clientEmail) {
      const isEmail = clientEmail.includes("@");
      dispatch(
        productClientActions.subscribeToProductClient(
          productId,
          isEmail ? clientEmail : null,
          isEmail ? undefined : clientEmail,
        ),
      );
    }

    return () => {
      dispatch(productClientActions.unsubscribeFromProductClient());
    };
  }, [productId, dispatch, clientEmail]);

  // Cennik produktu — wypełnia go tylko trasa `/products/:id/...`, a ten ekran
  // stoi też w drawerze na profilu klientki (TRE-1882). Bez niego akcje, które
  // czytają ceny — zmiana planu rat, dopłata do całości — cicho gubiły opcje:
  // wygaszony radiobutton „Cennik produktu" wypychał na kwotę ręczną, która nie
  // niesie `customDuration` docelowego pakietu (TRE-1970).
  useEffect(() => {
    if (productId && productDetails?.id !== productId) {
      void dispatch(productActions.fetchProduct(productId));
    }
  }, [productId, productDetails?.id, dispatch]);

  const clientName = data?.profile?.firstName
    ? `${data.profile.firstName} ${data.profile.lastName}`.trim()
    : `${data?.firstName} ${data?.lastName}`.trim();
  const archived = data?.status === "archived";

  const menu = (
    <Menu>
      <Menu.Item key="1" icon={<EditOutlined />} onClick={() => setClientToEmailChange(data)}>
        {t("ownerOptions.changeEmail")}
      </Menu.Item>
      <Menu.Item
        key="2"
        danger
        icon={<UserDeleteOutlined />}
        onClick={() => setClient(data)}
        disabled={archived && Boolean(data?.archivedInfo)}
      >
        {t("ownerOptions.refund")}
      </Menu.Item>
    </Menu>
  );

  const traineeId = data?.traineeId;

  const canSeeCustomers = usePermissionRequirement(dashboardPermissionRequirements.productsCustomers);
  // Starsze dostępy nie mają `customerId` - wtedy szukamy klienta po e-mailu.
  const { data: customerId } = useQuery({
    queryKey: ["productClientCustomerId", data?.customerId, data?.email],
    enabled: !embedded && canSeeCustomers && Boolean(data?.customerId || data?.email),
    queryFn: async () => {
      if (data?.customerId) return data.customerId;

      const { data: rows, error } = await supabase
        .from("customer")
        .select("id")
        // ILIKE bez znaków wieloznacznych: `_` w adresie dopasowałby cudzy e-mail.
        .ilike("email", (data?.email ?? "").replace(/[\\%_]/g, "\\$&"))
        .limit(1);

      if (error) throw new Error(error.message);
      return rows?.[0]?.id ?? null;
    },
  });

  return (
    <div>
      <Card
        loading={status === RequestStatus.SUBSCRIBING}
        title={
          embedded ? null : (
            <Button type="dashed" onClick={() => navigate(-1)}>
              <ArrowLeftOutlined />
            </Button>
          )
        }
        extra={
          <Space>
            {customerId && !embedded && (
              <Space.Compact>
                <Button icon={<IdcardOutlined />} onClick={() => navigate(`/products/customers/${customerId}`)}>
                  Profil klienta
                </Button>
                <Button
                  icon={<ExportOutlined />}
                  title="Otwórz w nowym oknie"
                  href={`${window.location.origin}/products/customers/${customerId}`}
                  target="_blank"
                />
              </Space.Compact>
            )}
            {canManageProducts && (
              <Dropdown overlay={menu} placement="bottomRight" disabled={readonly || archived}>
                <Button type="primary">
                  {t("common:options")} <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </Space>
        }
        bordered={!embedded}
      >
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          {data && (
            <Descriptions bordered labelStyle={{ width: 250 }}>
              <Descriptions.Item label={t("list.columns.client")} span={3}>
                <List.Item.Meta
                  style={{ alignItems: "center" }}
                  avatar={<Avatar src={data.profile?.avatarUrl}>{getUserInitials(clientName)}</Avatar>}
                  title={clientName}
                  description={<a href={`mailto:${data.email}`}>{data.email}</a>}
                />
              </Descriptions.Item>

              <Descriptions.Item label={t("list.columns.phoneNumber")} span={3}>
                {data.phoneNumber || "-"}
              </Descriptions.Item>

              <Descriptions.Item label={t("list.columns.date")} span={3}>
                {dayjs(data.createdAt).format("DD.MM.YYYY")}
              </Descriptions.Item>

              {data.productDuration && (
                <Descriptions.Item label={t("products:productDetails.productDurationType")} span={3}>
                  {data.productDuration.type === ProductDurationType.LastsFor
                    ? `${t(`products:recurring.${data.productDuration.unit}`, {
                        count: data.productDuration.count,
                      })} - ${dayjs.unix(data.productDuration.startAt).format("DD.MM.YYYY")} - ${dayjs
                        .unix(data.productDuration.endAt)
                        .format("DD.MM.YYYY")}`
                    : data.productDuration.type === ProductDurationType.UntilDate
                      ? `${dayjs.unix(data.productDuration.startAt).format("DD.MM.YYYY")} - ${dayjs
                          .unix(data.productDuration.endAt)
                          .format("DD.MM.YYYY")}`
                      : t(`products:productDurationType.${data.productDuration.type}`)}
                </Descriptions.Item>
              )}

              {data.trainee && traineeId && (
                <Descriptions.Item label={t("products:list.columns.traineeAccount")}>
                  <ClientStatusBadge status={data.trainee.status || TraineeStatus.INACTIVE} />
                  <Space>
                    <Button type="link" onClick={() => navigate(`/trainee/details/${traineeId}`)}>
                      Panel klienta
                    </Button>
                    <Button
                      style={{ marginLeft: -12 }}
                      icon={<ExportOutlined />}
                      title="Otwórz w nowy oknie"
                      href={`${window.location.origin}/trainee/details/${traineeId}`}
                      target="_blank"
                      type="link"
                    />
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </Space>
        <div>
          <div className="px-0 py-4">
            {status === RequestStatus.SUBSCRIBING ? (
              <Spin size="small" />
            ) : (
              <Elements stripe={stripePromise}>
                {/* `ownerView` bramkuje w ustawieniach wszystkie akcje właścicielskie
                    (anulowanie, przedłużenie, zmiana karty, plan rat) oraz dane
                    płatnicze — dlatego to ono, a nie `readonly`, niesie tu rolę. */}
                <ProductClientSettings
                  productClient={data}
                  ownerView={canManageProducts}
                  readonly={readonly || archived}
                />
              </Elements>
            )}
          </div>

          {canManageProducts &&
          data &&
          allowedPaymentTypes.includes(data.paymentType) &&
          data.invoiceRequested !== false ? (
            <div className="px-0 py-4">
              <ProductClientInvoiceData productClient={data} ownerView />
            </div>
          ) : null}
          {data ? (
            <div className="px-0 py-4">
              <ProductClientNotes productClientId={data.id} notes={data.notes} readonly={readonly} />
            </div>
          ) : null}
          {data ? (
            <div className="px-0 py-4">
              {/* Trener ma widzieć, czy klientka płaci: ostatnie 5 zamówień
                  z kwotami, kodami rabatowymi i statusem. Poza jego zasięgiem
                  zostają faktury i dane do faktury. */}
              <ProductClientActionsHistory
                ownerView
                productClient={data}
                showBillingData={canManageProducts}
                maxRows={canManageProducts ? undefined : 5}
              />
            </div>
          ) : null}
          {data && (scheduledAccess.length || hasInstallmentPlan) ? (
            <ProductClientScheduledAccess productClient={data} scheduledAccess={scheduledAccess} />
          ) : null}
          {data && (
            <div className="px-0 py-4">
              <ClientAutomationLogs productClient={data} ownerId={ownerId} />
            </div>
          )}
        </div>
      </Card>
      <ModalForm
        open={showArchiveModal}
        onCancel={() => setClient(null)}
        title={t("products:clientDetails.archiveModalTitle")}
        okButtonProps={{ danger: true }}
        okText={t("products:clientDetails.archiveConfirm")}
        width={950}
        loading={processing}
      >
        {selectedClient ? (
          <ProductClientArchive
            client={selectedClient}
            onSubmit={handleSubmit}
            lastOrder={lastOrder || null}
            productAutomation={productAutomation}
          />
        ) : (
          <div />
        )}
      </ModalForm>
      <ModalForm
        open={showChangeEmailModal}
        onCancel={() => setClientToEmailChange(null)}
        title={t("products:clientDetails.changeEmailModalTitle")}
        okText={t("products:clientDetails.confirm")}
        width={950}
        loading={processingChangingEmail}
      >
        {selectedClientToChangeEmail ? (
          <ProductClientEmailChange
            client={selectedClientToChangeEmail}
            onSubmit={handleEmailChangeSubmit}
            productAutomation={productAutomation}
          />
        ) : (
          <div />
        )}
      </ModalForm>
    </div>
  );
};

export default ProductClientDetails;
